import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  CheckCircle2,
  Eye,
  GripVertical,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  FundingPolicyOptionProjection,
  RenewalPolicyOptionProjection,
  ReviewTemplateProjection,
  ReviewTemplateUsageProjection,
  ScholarshipStandardProjection,
  WorkflowTemplateLibraryProjection,
} from "@/api/adminApi";
import {
  ApprovedWorkflowNode,
  ApprovedWorkflowTransition,
  TenantWorkflowDefinition,
  TenantWorkflowNode,
  TenantWorkflowTransition,
  TenantWorkflowVersion,
  WorkflowBusinessRules,
} from "@/api/tenantWorkflowApi";
import { Button } from "@/components/ui/button";

import {
  EmptyState,
  friendlyDate,
  PageHeader,
  Pill,
  ScholarshipStandardForm,
} from "./shared";
import {
  useTenantWorkflowWorkspace,
  WorkflowEditorState,
} from "./useTenantWorkflowWorkspace";

type Props = {
  tenantId: number | null;
  tenantName?: string | null;
  workflowTemplates: WorkflowTemplateLibraryProjection[];
  scholarshipStandards: ScholarshipStandardProjection[];
  olderReusableSetups: ReviewTemplateProjection[];
  selectedOlderSetupUsage: ReviewTemplateUsageProjection | null;
  olderSetupUsageLoading: boolean;
  retiringOlderSetup: ReviewTemplateProjection | null;
  fundingOptions: FundingPolicyOptionProjection[];
  renewalOptions: RenewalPolicyOptionProjection[];
  mentoringCriteria: Array<{ criterion: string; label: string }>;
  reusableSetupForm: ScholarshipStandardForm;
  savingReusableSetup: boolean;
  onReusableSetupFormChange: (form: ScholarshipStandardForm) => void;
  onSaveReusableSetup: () => Promise<boolean>;
  onOlderSetupUsage: (setup: ReviewTemplateProjection) => void;
  onOlderSetupEdit: (setup: ReviewTemplateProjection) => void;
  onOlderSetupActivate: (setup: ReviewTemplateProjection) => void;
  onOlderSetupVersion: (setup: ReviewTemplateProjection) => void;
  onOlderSetupRetireStart: (setup: ReviewTemplateProjection) => void;
  onOlderSetupRetireConfirm: () => void;
};

type CreateForm = {
  name: string;
  programCode: string;
  description: string;
  startingPoint: string;
};

const EMPTY_CREATE_FORM: CreateForm = {
  name: "",
  programCode: "",
  description: "",
  startingPoint: "",
};

export function WorkflowsSection(props: Props) {
  const workspace = useTenantWorkflowWorkspace(props.tenantId);
  const [creating, setCreating] = useState(false);
  const [creatingReusableSetup, setCreatingReusableSetup] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_CREATE_FORM);

  if (!props.tenantId) {
    return (
      <div>
        <PageHeader
          title="Workflows"
          subtitle="Select an organization before managing its workflows."
        />
        <div className="p-4">
          <EmptyState
            title="No organization selected"
            body="Choose an organization from the control center to view its workflows."
          />
        </div>
      </div>
    );
  }

  if (workspace.selectedDefinition && workspace.selectedVersion && workspace.editor) {
    return (
      <WorkflowEditor
        tenantName={props.tenantName}
        fundingOptions={props.fundingOptions}
        renewalOptions={props.renewalOptions}
        mentoringCriteria={props.mentoringCriteria}
        workspace={workspace}
      />
    );
  }

  async function createWorkflow() {
    if (createForm.name.trim().length < 3 || !createForm.programCode.trim()) return;
    const [source, rawId] = createForm.startingPoint.split(":");
    const sourceId = Number(rawId);
    const created = await workspace.createDefinition({
      name: createForm.name.trim(),
      program_code: createForm.programCode.trim(),
      description: createForm.description.trim() || null,
      seed_workflow_template_id:
        source === "template" && Number.isFinite(sourceId) ? sourceId : null,
      source_scholarship_standard_id:
        source === "standard" && Number.isFinite(sourceId) ? sourceId : null,
    });
    if (created) {
      setCreateForm(EMPTY_CREATE_FORM);
      setCreating(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Workflows"
        subtitle={`Build and publish review processes for ${props.tenantName ?? "this organization"}.`}
        action={
          <Button type="button" size="sm" onClick={() => setCreating((value) => !value)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Create workflow
          </Button>
        }
      />

      <WorkflowNotice error={workspace.error} message={workspace.message} />

      {creating ? (
        <div className="border-b bg-gray-50 p-4">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm font-medium">
              Workflow name
              <input
                className="mt-1 w-full rounded-md border px-3 py-2"
                value={createForm.name}
                onChange={(event) =>
                  setCreateForm((current) => ({ ...current, name: event.target.value }))
                }
              />
            </label>
            <label className="text-sm font-medium">
              Program code
              <input
                className="mt-1 w-full rounded-md border px-3 py-2"
                value={createForm.programCode}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    programCode: event.target.value,
                  }))
                }
              />
            </label>
            <label className="text-sm font-medium md:col-span-2">
              Start from
              <select
                className="mt-1 w-full rounded-md border bg-white px-3 py-2"
                value={createForm.startingPoint}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    startingPoint: event.target.value,
                  }))
                }
              >
                <option value="">Blank workflow</option>
                {props.workflowTemplates.map((template) => (
                  <option key={`template-${template.id}`} value={`template:${template.id}`}>
                    Recommended workflow · {template.name}
                  </option>
                ))}
                {props.scholarshipStandards.map((standard) => (
                  <option key={`standard-${standard.id}`} value={`standard:${standard.id}`}>
                    Reusable setup · {standard.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium md:col-span-2">
              Business description
              <textarea
                className="mt-1 min-h-20 w-full rounded-md border px-3 py-2"
                value={createForm.description}
                onChange={(event) =>
                  setCreateForm((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
            </label>
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={
                workspace.saving ||
                createForm.name.trim().length < 3 ||
                !createForm.programCode.trim()
              }
              onClick={() => void createWorkflow()}
            >
              Create draft
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setCreating(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      <div className="p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-950">
              Reusable workflow setups
            </h2>
            <p className="text-xs text-gray-600">
              Save common funding, renewal, mentoring, and process choices to start
              future workflows faster.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setCreatingReusableSetup((value) => !value)}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Add reusable setup
          </Button>
        </div>

        {creatingReusableSetup ? (
          <ReusableSetupForm
            form={props.reusableSetupForm}
            saving={props.savingReusableSetup}
            workflowTemplates={props.workflowTemplates}
            fundingOptions={props.fundingOptions}
            renewalOptions={props.renewalOptions}
            mentoringCriteria={props.mentoringCriteria}
            onChange={props.onReusableSetupFormChange}
            onSave={() => {
              void props.onSaveReusableSetup().then((saved) => {
                if (saved) setCreatingReusableSetup(false);
              });
            }}
            onCancel={() => setCreatingReusableSetup(false)}
          />
        ) : null}

        <ReusableSetupList
          setups={props.scholarshipStandards}
          workflowTemplates={props.workflowTemplates}
          olderSetups={props.olderReusableSetups}
          usage={props.selectedOlderSetupUsage}
          usageLoading={props.olderSetupUsageLoading}
          retiringSetup={props.retiringOlderSetup}
          retireReason={props.reusableSetupForm.retire_reason}
          retireConfirmed={props.reusableSetupForm.retire_confirm}
          saving={props.savingReusableSetup}
          onRetireFormChange={(retire_reason, retire_confirm) =>
            props.onReusableSetupFormChange({
              ...props.reusableSetupForm,
              retire_reason,
              retire_confirm,
            })
          }
          onUsage={props.onOlderSetupUsage}
          onEdit={(setup) => {
            props.onOlderSetupEdit(setup);
            setCreatingReusableSetup(true);
          }}
          onActivate={props.onOlderSetupActivate}
          onVersion={props.onOlderSetupVersion}
          onRetireStart={props.onOlderSetupRetireStart}
          onRetireConfirm={props.onOlderSetupRetireConfirm}
        />

        {workspace.loading ? (
          <div className="py-8 text-center text-sm text-gray-600">Loading workflows...</div>
        ) : workspace.definitions.length === 0 ? (
          <EmptyState
            title="No workflows yet"
            body="Create the first workflow for this organization and program."
          />
        ) : (
          <WorkflowList
            definitions={workspace.definitions}
            onOpen={workspace.chooseDefinition}
          />
        )}
      </div>
    </div>
  );
}

function ReusableSetupForm({
  form,
  saving,
  workflowTemplates,
  fundingOptions,
  renewalOptions,
  mentoringCriteria,
  onChange,
  onSave,
  onCancel,
}: {
  form: ScholarshipStandardForm;
  saving: boolean;
  workflowTemplates: WorkflowTemplateLibraryProjection[];
  fundingOptions: FundingPolicyOptionProjection[];
  renewalOptions: RenewalPolicyOptionProjection[];
  mentoringCriteria: Props["mentoringCriteria"];
  onChange: (form: ScholarshipStandardForm) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const selectedTemplate =
    workflowTemplates.find((template) => String(template.id) === form.workflow_template_id) ??
    workflowTemplates[0];
  const fundingYears =
    fundingOptions.find((option) => option.funding_type === form.funding_type)
      ?.allowed_years ?? (form.funding_type === "single_year" ? [1] : [2, 3, 4, 5]);

  return (
    <section className="mb-4 rounded-md border bg-gray-50 p-3">
      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-sm font-medium">
          Setup name
          <input
            className="mt-1 w-full rounded-md border bg-white px-3 py-2"
            value={form.template_code}
            onChange={(event) =>
              onChange({ ...form, template_code: event.target.value, edit_template_id: null })
            }
            placeholder="Example: Saksham 2026"
          />
        </label>
        <label className="text-sm font-medium">
          Recommended workflow
          <select
            className="mt-1 w-full rounded-md border bg-white px-3 py-2"
            value={form.workflow_template_id || String(workflowTemplates[0]?.id ?? "")}
            onChange={(event) => {
              const nextTemplate = workflowTemplates.find(
                (template) => String(template.id) === event.target.value
              );
              onChange({
                ...form,
                workflow_template_id: event.target.value,
                mentoring_completion_criteria: nextTemplate?.requires_mentoring
                  ? form.mentoring_completion_criteria
                  : [],
              });
            }}
          >
            {workflowTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-medium">
          Funding
          <select
            className="mt-1 w-full rounded-md border bg-white px-3 py-2"
            value={form.funding_type}
            onChange={(event) => {
              const fundingType = event.target.value as ScholarshipStandardForm["funding_type"];
              onChange({
                ...form,
                funding_type: fundingType,
                number_of_years: fundingType === "multi_year" ? "2" : "1",
              });
            }}
          >
            {fundingOptions.length > 0 ? (
              fundingOptions.map((option) => (
                <option key={option.funding_type} value={option.funding_type}>
                  {option.name}
                </option>
              ))
            ) : (
              <>
                <option value="single_year">Single year</option>
                <option value="multi_year">Multi-year</option>
              </>
            )}
          </select>
        </label>
        <label className="text-sm font-medium">
          Years
          <select
            className="mt-1 w-full rounded-md border bg-white px-3 py-2"
            value={form.number_of_years}
            disabled={form.funding_type === "single_year"}
            onChange={(event) => onChange({ ...form, number_of_years: event.target.value })}
          >
            {fundingYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm font-medium md:col-span-2">
          Renewal policy
          <select
            className="mt-1 w-full rounded-md border bg-white px-3 py-2"
            value={form.renewal_rule}
            onChange={(event) => onChange({ ...form, renewal_rule: event.target.value })}
          >
            {renewalOptions.map((option) => (
              <option key={option.renewal_rule} value={option.renewal_rule}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {selectedTemplate?.requires_mentoring ? (
        <fieldset className="mt-3 rounded-md border bg-white p-3">
          <legend className="px-1 text-sm font-medium">
            Mentoring completion criteria
          </legend>
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            {mentoringCriteria.map((option) => {
              const checked = form.mentoring_completion_criteria.includes(option.criterion);
              return (
                <label key={option.criterion} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={checked}
                    onChange={(event) =>
                      onChange({
                        ...form,
                        mentoring_completion_criteria: event.target.checked
                          ? [...form.mentoring_completion_criteria, option.criterion]
                          : form.mentoring_completion_criteria.filter(
                              (item) => item !== option.criterion
                            ),
                      })
                    }
                  />
                  {option.label}
                </label>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      <label className="mt-3 block text-sm font-medium">
        Business description
        <textarea
          className="mt-1 min-h-20 w-full rounded-md border bg-white px-3 py-2"
          value={form.description}
          onChange={(event) => onChange({ ...form, description: event.target.value })}
          placeholder="Explain when this setup should be used."
        />
      </label>

      <div className="mt-3 flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={saving || workflowTemplates.length === 0}
          onClick={onSave}
        >
          Save reusable setup
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  );
}

function ReusableSetupList({
  setups,
  workflowTemplates,
  olderSetups,
  usage,
  usageLoading,
  retiringSetup,
  retireReason,
  retireConfirmed,
  saving,
  onRetireFormChange,
  onUsage,
  onEdit,
  onActivate,
  onVersion,
  onRetireStart,
  onRetireConfirm,
}: {
  setups: ScholarshipStandardProjection[];
  workflowTemplates: WorkflowTemplateLibraryProjection[];
  olderSetups: ReviewTemplateProjection[];
  usage: ReviewTemplateUsageProjection | null;
  usageLoading: boolean;
  retiringSetup: ReviewTemplateProjection | null;
  retireReason: string;
  retireConfirmed: boolean;
  saving: boolean;
  onRetireFormChange: (reason: string, confirmed: boolean) => void;
  onUsage: (setup: ReviewTemplateProjection) => void;
  onEdit: (setup: ReviewTemplateProjection) => void;
  onActivate: (setup: ReviewTemplateProjection) => void;
  onVersion: (setup: ReviewTemplateProjection) => void;
  onRetireStart: (setup: ReviewTemplateProjection) => void;
  onRetireConfirm: () => void;
}) {
  return (
    <div className="mb-5 overflow-hidden rounded-md border">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead className="bg-gray-50 text-xs text-gray-600">
            <tr>
              <th className="px-3 py-2 font-medium">Setup</th>
              <th className="px-3 py-2 font-medium">Recommended workflow</th>
              <th className="px-3 py-2 font-medium">Funding</th>
              <th className="px-3 py-2 font-medium">Renewal</th>
              <th className="px-3 py-2 font-medium">Mentoring</th>
              <th className="px-3 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {setups.map((setup) => (
              <tr key={setup.id}>
                <td className="px-3 py-3">
                  <div className="font-medium text-gray-950">{setup.name}</div>
                  <div className="text-xs text-gray-500">
                    {setup.description || setup.standard_code}
                  </div>
                </td>
                <td className="px-3 py-3">{setup.workflow_template.name}</td>
                <td className="px-3 py-3">
                  {setup.funding_policy.funding_type === "multi_year"
                    ? `${setup.funding_policy.number_of_years} years`
                    : "Single year"}
                </td>
                <td className="px-3 py-3">{setup.renewal_policy.name}</td>
                <td className="px-3 py-3">
                  {setup.mentoring_policy.mentoring_required ? (
                    <Pill tone="teal">
                      {setup.mentoring_policy.completion_criteria.length} criteria
                    </Pill>
                  ) : (
                    <Pill>No mentoring</Pill>
                  )}
                </td>
                <td className="px-3 py-3">
                  <Pill tone={setup.lifecycle_state === "active" ? "green" : "slate"}>
                    {friendlyCode(setup.lifecycle_state)}
                  </Pill>
                </td>
              </tr>
            ))}
            {setups.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-6 text-center text-sm text-gray-500">
                  No reusable setups saved for this organization.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {workflowTemplates.length > 0 ? (
        <details className="border-t bg-gray-50 px-3 py-2 text-sm">
          <summary className="cursor-pointer font-medium text-gray-900">
            Recommended workflows
          </summary>
          <div className="mt-2 space-y-2">
            {workflowTemplates.map((template) => (
              <div key={template.id} className="rounded-md border bg-white p-2">
                <div className="font-medium">{template.name}</div>
                <div className="text-xs text-gray-500">{template.description}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {template.path.map((step) => (
                    <span
                      key={`${template.id}-${step}`}
                      className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-800"
                    >
                      {friendlyCode(step)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </details>
      ) : null}

      {olderSetups.length > 0 ? (
        <details className="border-t bg-gray-50 px-3 py-2 text-sm">
          <summary className="cursor-pointer font-medium text-gray-900">
            Older reusable setups
          </summary>
          <div className="mt-2 overflow-x-auto rounded-md border bg-white">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600">
                <tr>
                  <th className="px-3 py-2 font-medium">Setup</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Version</th>
                  <th className="px-3 py-2 font-medium">Use</th>
                  <th className="px-3 py-2 font-medium"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {olderSetups.map((setup) => (
                  <tr key={setup.id}>
                    <td className="px-3 py-3">
                      <div className="font-medium">{friendlyCode(setup.template_code)}</div>
                      <div className="text-xs text-gray-500">
                        {setup.description || setup.template_code}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <Pill tone={setup.lifecycle_state === "active" ? "green" : "slate"}>
                        {friendlyCode(setup.lifecycle_state)}
                      </Pill>
                    </td>
                    <td className="px-3 py-3">v{setup.version}</td>
                    <td className="px-3 py-3">
                      {setup.usage.review_processes_derived} workflows
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => onUsage(setup)}>
                          Usage
                        </Button>
                        {setup.lifecycle_state === "draft" ? (
                          <>
                            <Button type="button" size="sm" variant="outline" onClick={() => onEdit(setup)}>
                              Edit
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => onActivate(setup)}>
                              Activate
                            </Button>
                          </>
                        ) : null}
                        <Button type="button" size="sm" variant="outline" onClick={() => onVersion(setup)}>
                          New version
                        </Button>
                        {setup.lifecycle_state === "active" ? (
                          <Button type="button" size="sm" variant="outline" onClick={() => onRetireStart(setup)}>
                            Retire
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {usageLoading ? (
            <div className="mt-2 text-xs text-gray-600">Loading usage...</div>
          ) : null}
          {usage ? <OlderSetupUsage usage={usage} /> : null}
          {retiringSetup ? (
            <div className="mt-3 rounded-md border bg-white p-3">
              <div className="text-sm font-semibold">
                Retire {friendlyCode(retiringSetup.template_code)}
              </div>
              <p className="mt-1 text-xs text-gray-600">
                Retirement blocks future use only. Existing programs and applications stay unchanged.
              </p>
              <label className="mt-2 block text-xs font-medium">
                Reason
                <textarea
                  className="mt-1 min-h-16 w-full rounded-md border px-2 py-1"
                  value={retireReason}
                  onChange={(event) => onRetireFormChange(event.target.value, retireConfirmed)}
                />
              </label>
              <label className="mt-2 flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={retireConfirmed}
                  onChange={(event) => onRetireFormChange(retireReason, event.target.checked)}
                />
                I understand this affects future use only.
              </label>
              <Button
                type="button"
                size="sm"
                className="mt-2"
                disabled={saving}
                onClick={onRetireConfirm}
              >
                Confirm retirement
              </Button>
            </div>
          ) : null}
        </details>
      ) : null}
    </div>
  );
}

function OlderSetupUsage({ usage }: { usage: ReviewTemplateUsageProjection }) {
  const summary = usage.usage;
  return (
    <div className="mt-3 rounded-md border bg-white p-3">
      <div className="text-sm font-semibold">Usage</div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        <UsageMetric label="Organizations" value={summary.organizations_using_template} />
        <UsageMetric label="Programs" value={summary.programs_using_template} />
        <UsageMetric label="Workflows created" value={summary.review_processes_derived} />
        <UsageMetric label="Active applications" value={summary.applications_governed} />
        <UsageMetric label="Historical applications" value={summary.historical_applications_governed} />
      </div>
      {usage.programs.length > 0 ? (
        <div className="mt-2 text-xs text-gray-600">
          {usage.programs.map((program) => program.program_code).join(", ")}
        </div>
      ) : null}
    </div>
  );
}

function UsageMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border bg-gray-50 p-2">
      <div className="text-base font-semibold text-gray-950">{value}</div>
      <div className="text-xs text-gray-600">{label}</div>
    </div>
  );
}

function WorkflowList({
  definitions,
  onOpen,
}: {
  definitions: TenantWorkflowDefinition[];
  onOpen: (definition: TenantWorkflowDefinition) => void;
}) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full min-w-[760px] text-left text-sm">
        <thead className="bg-gray-50 text-xs text-gray-600">
          <tr>
            <th className="px-3 py-2 font-medium">Workflow</th>
            <th className="px-3 py-2 font-medium">Program</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Current version</th>
            <th className="px-3 py-2 font-medium">Last updated</th>
            <th className="px-3 py-2 font-medium"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {definitions.map((definition) => {
            const versions = [...definition.versions].sort(
              (left, right) => right.version - left.version
            );
            const current =
              versions.find((version) => version.lifecycle_state === "draft") ?? versions[0];
            const published = versions.find(
              (version) => version.lifecycle_state === "published"
            );
            return (
              <tr key={definition.id}>
                <td className="px-3 py-3">
                  <div className="font-medium text-gray-950">{definition.name}</div>
                  <div className="text-xs text-gray-500">{definition.description || "No description"}</div>
                </td>
                <td className="px-3 py-3">{definition.program_code}</td>
                <td className="px-3 py-3"><LifecyclePill state={current?.lifecycle_state} /></td>
                <td className="px-3 py-3">
                  {published ? `Published v${published.version}` : `Version ${current?.version ?? 1}`}
                </td>
                <td className="px-3 py-3">{friendlyDate(definition.updated_at)}</td>
                <td className="px-3 py-3 text-right">
                  <Button type="button" size="sm" variant="outline" onClick={() => onOpen(definition)}>
                    Open
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type Workspace = ReturnType<typeof useTenantWorkflowWorkspace>;

function WorkflowEditor({
  tenantName,
  fundingOptions,
  renewalOptions,
  mentoringCriteria,
  workspace,
}: {
  tenantName?: string | null;
  fundingOptions: Props["fundingOptions"];
  renewalOptions: Props["renewalOptions"];
  mentoringCriteria: Props["mentoringCriteria"];
  workspace: Workspace;
}) {
  const definition = workspace.selectedDefinition!;
  const version = workspace.selectedVersion!;
  const editor = workspace.editor!;
  const readOnly = version.lifecycle_state !== "draft";
  const firstVersionNodeCode = [...version.nodes].sort(
    (left, right) => left.display_order - right.display_order
  )[0]?.node_code;
  const [selectedNodeCode, setSelectedNodeCode] = useState<string | null>(
    firstVersionNodeCode ?? null
  );
  const [draggedNodeCode, setDraggedNodeCode] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [connectionChoice, setConnectionChoice] = useState("");
  const [selectedConnectionKey, setSelectedConnectionKey] = useState<string | null>(null);

  useEffect(() => {
    setSelectedNodeCode(firstVersionNodeCode ?? null);
    setSelectedConnectionKey(null);
    setPreviewOpen(false);
  }, [firstVersionNodeCode, version.id]);

  const approvedNodes = workspace.components?.nodes ?? [];
  const approvedTransitions = workspace.components?.transitions ?? [];
  const selectedNode = editor.nodes.find((node) => node.node_code === selectedNodeCode) ?? null;
  const selectedCodes = new Set(editor.nodes.map((node) => node.node_code));
  const hasInterviewStage = selectedCodes.has("interview");
  const hasMentorshipStage = selectedCodes.has("mentorship");
  const hasClientGovernanceMove = editor.transitions.some(requiresClientGovernance);
  const existingConnections = new Set(
    editor.transitions.map(transitionKey)
  );
  const availableConnections = approvedTransitions.filter(
    (transition) =>
      selectedCodes.has(transition.from_status) &&
      selectedCodes.has(transition.to_status) &&
      !existingConnections.has(transitionKey(transition))
  );

  function updateEditor(next: Partial<WorkflowEditorState>) {
    workspace.setEditor({ ...editor, ...next });
  }

  function businessRulesForSelection(
    nodes: TenantWorkflowNode[],
    transitions: TenantWorkflowTransition[]
  ): WorkflowBusinessRules {
    const nodeCodes = new Set(nodes.map((node) => node.node_code));
    const interviewRequired = nodeCodes.has("interview");
    const mentoringRequired = nodeCodes.has("mentorship");
    const clientGovernanceRequired = transitions.some(requiresClientGovernance);
    return {
      ...editor.businessRules,
      interview: { required: interviewRequired },
      mentoring: {
        ...editor.businessRules.mentoring,
        required: mentoringRequired,
        completion_criteria: mentoringRequired
          ? editor.businessRules.mentoring.completion_criteria
          : [],
      },
      approvals: {
        ...editor.businessRules.approvals,
        management_required: true,
        client_governance_required: clientGovernanceRequired,
      },
    };
  }

  useEffect(() => {
    if (
      editor.businessRules.interview.required === hasInterviewStage &&
      editor.businessRules.mentoring.required === hasMentorshipStage &&
      editor.businessRules.approvals.client_governance_required === hasClientGovernanceMove
    ) {
      return;
    }
    workspace.setEditor({
      ...editor,
      businessRules: businessRulesForSelection(editor.nodes, editor.transitions),
    });
    // This repairs older drafts whose requirements do not match their process stages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editor.businessRules.interview.required,
    editor.businessRules.mentoring.required,
    editor.businessRules.approvals.client_governance_required,
    hasInterviewStage,
    hasMentorshipStage,
    hasClientGovernanceMove,
  ]);

  function addStage(stage: ApprovedWorkflowNode) {
    if (readOnly || selectedCodes.has(stage.node_code)) return;
    const node: TenantWorkflowNode = {
      id: -Date.now(),
      node_code: stage.node_code,
      stage_code: stage.stage_code,
      display_name: stage.name,
      display_order: editor.nodes.length,
      is_start: stage.is_start,
      is_terminal: stage.is_terminal,
    };
    const nodes = [...editor.nodes, node];
    updateEditor({ nodes, businessRules: businessRulesForSelection(nodes, editor.transitions) });
    setSelectedNodeCode(node.node_code);
  }

  function removeStage(node: TenantWorkflowNode) {
    if (readOnly || node.is_start) return;
    const nodes = editor.nodes.filter((item) => item.node_code !== node.node_code);
    const transitions = editor.transitions.filter(
      (item) => item.from_status !== node.node_code && item.to_status !== node.node_code
    );
    updateEditor({
      nodes,
      transitions,
      businessRules: businessRulesForSelection(nodes, transitions),
    });
    setSelectedNodeCode(editor.nodes.find((item) => item.node_code !== node.node_code)?.node_code ?? null);
  }

  function reorder(sourceCode: string, targetCode: string) {
    if (readOnly || sourceCode === targetCode) return;
    const sourceIndex = editor.nodes.findIndex((node) => node.node_code === sourceCode);
    const targetIndex = editor.nodes.findIndex((node) => node.node_code === targetCode);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const next = [...editor.nodes];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    updateEditor({
      nodes: next.map((node, index) => ({ ...node, display_order: index })),
    });
  }

  function moveStage(nodeCode: string, direction: -1 | 1) {
    const index = editor.nodes.findIndex((node) => node.node_code === nodeCode);
    const target = editor.nodes[index + direction];
    if (target) reorder(nodeCode, target.node_code);
  }

  function addConnection() {
    const approved = availableConnections.find(
      (transition) => transitionKey(transition) === connectionChoice
    );
    if (!approved || readOnly) return;
    const transitions = [
      ...editor.transitions,
      { ...approved, id: -Date.now() },
    ];
    updateEditor({
      transitions,
      businessRules: businessRulesForSelection(editor.nodes, transitions),
    });
    setConnectionChoice("");
  }

  function setRules(rules: WorkflowBusinessRules) {
    updateEditor({ businessRules: rules });
  }

  return (
    <div>
      <div className="border-b bg-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <Button type="button" variant="outline" size="sm" className="h-8 w-8 p-0" onClick={workspace.closeEditor} title="Back to workflows">
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-lg font-semibold text-gray-950">{definition.name}</h1>
                <LifecyclePill state={version.lifecycle_state} />
                {workspace.dirty ? <Pill tone="amber">Unsaved changes</Pill> : null}
              </div>
              <p className="text-xs text-gray-600">
                {tenantName ?? "Organization"} · {definition.program_code} · Version {version.version}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs font-medium text-gray-600">
              Version
              <select
                className="ml-2 rounded-md border bg-white px-2 py-1.5 text-sm text-gray-900"
                value={version.id}
                onChange={(event) => {
                  const next = definition.versions.find((item) => item.id === Number(event.target.value));
                  if (next) workspace.chooseVersion(definition, next);
                }}
              >
                {[...definition.versions]
                  .sort((left, right) => right.version - left.version)
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      v{item.version} · {friendlyLifecycle(item.lifecycle_state)}
                    </option>
                  ))}
              </select>
            </label>
            <Button type="button" size="sm" variant="outline" onClick={() => setPreviewOpen((value) => !value)}>
              <Eye className="mr-2 h-4 w-4" aria-hidden="true" /> Preview
            </Button>
            {version.lifecycle_state === "draft" ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={workspace.saving}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Delete draft v${version.version} of ${definition.name}? This cannot be undone.`
                      )
                    ) {
                      void workspace.deleteDraft();
                    }
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" /> Delete draft
                </Button>
                <Button type="button" size="sm" variant="outline" disabled={workspace.saving || !workspace.dirty} onClick={() => void workspace.saveDraft()}>
                  <Save className="mr-2 h-4 w-4" aria-hidden="true" /> Save draft
                </Button>
                <Button type="button" size="sm" disabled={workspace.saving} onClick={() => void workspace.validateDraft()}>
                  <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden="true" /> Validate
                </Button>
              </>
            ) : null}
            {version.lifecycle_state === "validated" ? (
              <Button type="button" size="sm" disabled={workspace.saving} onClick={() => void workspace.publishVersion()}>
                Publish
              </Button>
            ) : null}
            {version.lifecycle_state === "published" || version.lifecycle_state === "superseded" ? (
              <Button type="button" size="sm" onClick={() => void workspace.startNewVersion(version)} disabled={workspace.saving}>
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" /> New version
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <WorkflowNotice error={workspace.error} message={workspace.message} />

      {readOnly ? (
        <div className="border-b border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800">
          {version.lifecycle_state === "validated"
            ? "This validated version is locked and ready to publish."
            : "Published workflow versions are read-only. Create a new version to make changes."}
        </div>
      ) : null}

      {previewOpen ? <WorkflowPreview editor={editor} /> : null}

      <div className="grid min-h-[620px] lg:grid-cols-[240px_minmax(360px,1fr)_320px]">
        <aside className="border-b bg-gray-50 p-3 lg:border-b-0 lg:border-r">
          <h2 className="text-sm font-semibold text-gray-950">Approved stages</h2>
          <div className="mt-3 space-y-2">
            {approvedNodes.map((stage) => {
              const added = selectedCodes.has(stage.node_code);
              return (
                <div key={stage.node_code} className="flex items-center justify-between gap-2 rounded-md border bg-white p-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{stage.name}</div>
                    <div className="text-xs text-gray-500">{stage.stage_code}</div>
                  </div>
                  <Button type="button" size="sm" className="h-8 w-8 p-0" variant="outline" disabled={readOnly || added} onClick={() => addStage(stage)} title={`Add ${stage.name}`}>
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              );
            })}
          </div>
        </aside>

        <main className="min-w-0 border-b p-3 lg:border-b-0 lg:border-r">
          <h2 className="text-sm font-semibold text-gray-950">Process</h2>
          {editor.nodes.length === 0 ? (
            <EmptyState title="Empty workflow" body="Add approved stages from the stage list." />
          ) : (
            <div className="mt-3 space-y-2" aria-label="Workflow stages">
              {editor.nodes.map((node, index) => {
                const approved = approvedNodes.find((item) => item.node_code === node.node_code);
                return (
                  <div key={node.node_code}>
                    <div
                      data-testid={`workflow-stage-${node.node_code}`}
                      draggable={!readOnly}
                      onDragStart={() => setDraggedNodeCode(node.node_code)}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => {
                        if (draggedNodeCode) reorder(draggedNodeCode, node.node_code);
                        setDraggedNodeCode(null);
                      }}
                      className={`flex items-center gap-2 rounded-md border p-2 ${
                        selectedNodeCode === node.node_code
                          ? "border-indigo-500 bg-indigo-50"
                          : "bg-white"
                      }`}
                    >
                      <GripVertical className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                      <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setSelectedNodeCode(node.node_code)}>
                        <div className="truncate text-sm font-medium text-gray-950">{node.display_name}</div>
                        <div className="text-xs text-gray-500">
                          {approved?.stage_code ?? "Unavailable stage"}
                          {node.is_start ? " · Start" : ""}
                          {node.is_terminal ? " · Terminal" : ""}
                        </div>
                      </button>
                      {!readOnly ? (
                        <div className="flex gap-1">
                          <Button type="button" size="sm" className="h-8 w-8 border-0 p-0" variant="outline" disabled={index === 0} onClick={() => moveStage(node.node_code, -1)} title="Move stage up">
                            <ArrowUp className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button type="button" size="sm" className="h-8 w-8 border-0 p-0" variant="outline" disabled={index === editor.nodes.length - 1} onClick={() => moveStage(node.node_code, 1)} title="Move stage down">
                            <ArrowDown className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button type="button" size="sm" className="h-8 w-8 border-0 p-0" variant="outline" disabled={node.is_start} onClick={() => removeStage(node)} title="Remove stage">
                            <Trash2 className="h-4 w-4 text-red-600" aria-hidden="true" />
                          </Button>
                        </div>
                      ) : null}
                    </div>
                    {index < editor.nodes.length - 1 ? (
                      <div className="flex justify-center py-0.5"><ArrowDown className="h-4 w-4 text-gray-400" aria-hidden="true" /></div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-5 border-t pt-4">
            <h2 className="text-sm font-semibold text-gray-950">Allowed moves</h2>
            <div className="mt-2 space-y-2">
              {editor.transitions.map((transition) => (
                <div key={transitionKey(transition)} className={`flex items-center gap-2 rounded-md border p-2 text-sm ${selectedConnectionKey === transitionKey(transition) ? "border-indigo-500 bg-indigo-50" : "bg-white"}`}>
                  <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => setSelectedConnectionKey(transitionKey(transition))}>
                    <span className="font-medium">{friendlyCode(transition.from_status)}</span>
                    <ArrowRight className="h-4 w-4 text-gray-400" aria-hidden="true" />
                    <span className="font-medium">{friendlyCode(transition.to_status)}</span>
                    <span className="ml-auto text-xs text-gray-500">{friendlyCode(transition.action_code)}</span>
                  </button>
                  {!readOnly ? (
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 w-8 border-0 p-0"
                      variant="outline"
                      title="Remove move"
                      onClick={() => {
                        const transitions = editor.transitions.filter(
                          (item) => transitionKey(item) !== transitionKey(transition)
                        );
                        updateEditor({
                          transitions,
                          businessRules: businessRulesForSelection(editor.nodes, transitions),
                        });
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" aria-hidden="true" />
                    </Button>
                  ) : null}
                </div>
              ))}
              {editor.transitions.length === 0 ? <p className="text-sm text-gray-500">No allowed moves added.</p> : null}
            </div>
            {!readOnly ? (
              <div className="mt-3 flex gap-2">
                <label className="sr-only" htmlFor="approved-connection">Approved move</label>
                <select id="approved-connection" className="min-w-0 flex-1 rounded-md border bg-white px-3 py-2 text-sm" value={connectionChoice} onChange={(event) => setConnectionChoice(event.target.value)}>
                  <option value="">Choose allowed move</option>
                  {availableConnections.map((transition) => (
                    <option key={transitionKey(transition)} value={transitionKey(transition)}>
                      {friendlyCode(transition.from_status)} → {friendlyCode(transition.to_status)} · {friendlyCode(transition.action_code)}
                    </option>
                  ))}
                </select>
                <Button type="button" size="sm" variant="outline" disabled={!connectionChoice} onClick={addConnection}>Add</Button>
              </div>
            ) : null}
          </div>
        </main>

        <aside className="bg-white p-3">
          <h2 className="text-sm font-semibold text-gray-950">Configuration</h2>
          {selectedNode ? (
            <div className="mt-3 border-b pb-4">
              <label className="text-sm font-medium">
                Stage name
                <input
                  className="mt-1 w-full rounded-md border px-3 py-2"
                  disabled={readOnly}
                  value={selectedNode.display_name}
                  onChange={(event) => updateEditor({ nodes: editor.nodes.map((node) => node.node_code === selectedNode.node_code ? { ...node, display_name: event.target.value } : node) })}
                />
              </label>
              <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div><dt className="text-gray-500">Stage</dt><dd className="font-medium">{selectedNode.stage_code}</dd></div>
                <div><dt className="text-gray-500">Code</dt><dd className="font-medium">{selectedNode.node_code}</dd></div>
              </dl>
            </div>
          ) : null}

          {selectedConnectionKey ? (
            <TransitionAuthority
              transition={editor.transitions.find(
                (item) => transitionKey(item) === selectedConnectionKey
              )}
            />
          ) : null}

          <BusinessRulesEditor
            rules={editor.businessRules}
            readOnly={readOnly}
            fundingOptions={fundingOptions}
            renewalOptions={renewalOptions}
            mentoringCriteria={mentoringCriteria}
            onChange={setRules}
          />
        </aside>
      </div>
    </div>
  );
}

function TransitionAuthority({
  transition,
}: {
  transition?: TenantWorkflowTransition;
}) {
  if (!transition) return null;
  return (
    <div className="border-b py-4">
      <h3 className="text-sm font-semibold">Move details</h3>
      <dl className="mt-2 space-y-2 text-xs">
        <div>
          <dt className="text-gray-500">Action</dt>
          <dd className="font-medium">{friendlyCode(transition.action_code)}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Responsible role</dt>
          <dd className="font-medium">{friendlyCode(transition.owner_role)}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Allowed roles</dt>
          <dd className="font-medium">
            {transition.allowed_roles.map(friendlyCode).join(", ") || "None"}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function BusinessRulesEditor({
  rules,
  readOnly,
  fundingOptions,
  renewalOptions,
  mentoringCriteria,
  onChange,
}: {
  rules: WorkflowBusinessRules;
  readOnly: boolean;
  fundingOptions: Props["fundingOptions"];
  renewalOptions: Props["renewalOptions"];
  mentoringCriteria: Props["mentoringCriteria"];
  onChange: (rules: WorkflowBusinessRules) => void;
}) {
  function patch<K extends keyof WorkflowBusinessRules>(
    section: K,
    value: WorkflowBusinessRules[K]
  ) {
    onChange({ ...rules, [section]: value });
  }

  const fundingYears =
    fundingOptions.find((option) => option.funding_type === rules.funding.funding_type)
      ?.allowed_years ?? (rules.funding.funding_type === "single_year" ? [1] : [2, 3, 4, 5]);

  return (
    <div className="mt-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold">Funding and renewal</h3>
        <label className="mt-2 block text-xs font-medium">
          Funding
          <select className="mt-1 w-full rounded-md border bg-white px-2 py-2 text-sm" disabled={readOnly} value={rules.funding.funding_type} onChange={(event) => {
            const fundingType = event.target.value as "single_year" | "multi_year";
            patch("funding", { funding_type: fundingType, number_of_years: fundingType === "single_year" ? 1 : 2 });
          }}>
            {fundingOptions.length > 0 ? fundingOptions.map((option) => (
              <option key={option.funding_type} value={option.funding_type}>{option.name}</option>
            )) : <><option value="single_year">Single year</option><option value="multi_year">Multi-year</option></>}
          </select>
        </label>
        <label className="mt-2 block text-xs font-medium">
          Years
          <select className="mt-1 w-full rounded-md border bg-white px-2 py-2 text-sm" disabled={readOnly} value={rules.funding.number_of_years} onChange={(event) => patch("funding", { ...rules.funding, number_of_years: Number(event.target.value) })}>
            {fundingYears.map((year) => <option key={year} value={year}>{year}</option>)}
          </select>
        </label>
        <label className="mt-2 block text-xs font-medium">
          Renewal policy
          <select className="mt-1 w-full rounded-md border bg-white px-2 py-2 text-sm" disabled={readOnly} value={rules.renewal.renewal_rule} onChange={(event) => patch("renewal", { renewal_rule: event.target.value })}>
            {renewalOptions.length > 0 ? renewalOptions.map((option) => <option key={option.renewal_rule} value={option.renewal_rule}>{option.name}</option>) : <option value={rules.renewal.renewal_rule}>{friendlyCode(rules.renewal.renewal_rule)}</option>}
          </select>
        </label>
      </div>

      <div className="border-t pt-4">
        <h3 className="text-sm font-semibold">Review requirements</h3>
        <Toggle label="Interview required" checked={rules.interview.required} disabled onChange={() => undefined} />
        <Toggle label="Mentoring required" checked={rules.mentoring.required} disabled onChange={() => undefined} />
        {rules.mentoring.required ? (
          <div className="mt-2 space-y-1">
            {mentoringCriteria.map((criterion) => (
              <label key={criterion.criterion} className="flex items-start gap-2 text-xs">
                <input type="checkbox" className="mt-0.5" disabled={readOnly} checked={rules.mentoring.completion_criteria.includes(criterion.criterion)} onChange={(event) => patch("mentoring", { ...rules.mentoring, completion_criteria: event.target.checked ? [...rules.mentoring.completion_criteria, criterion.criterion] : rules.mentoring.completion_criteria.filter((item) => item !== criterion.criterion) })} />
                {criterion.label}
              </label>
            ))}
          </div>
        ) : null}
        <Toggle label="Management approval" checked={rules.approvals.management_required} disabled onChange={() => undefined} />
        <Toggle label="Client governance" checked={rules.approvals.client_governance_required} disabled onChange={() => undefined} />
      </div>

      <EvidenceRulesEditor
        value={rules.evidence}
        readOnly={readOnly}
        onChange={(value) => patch("evidence", value)}
      />
    </div>
  );
}

function EvidenceRulesEditor({ value, readOnly, onChange }: { value: Record<string, unknown>; readOnly: boolean; onChange: (value: Record<string, unknown>) => void }) {
  const documents = requiredDocumentCodes(value);
  function setDocuments(next: string[]) {
    onChange({ required: next });
  }
  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Evidence requirements</h3>
        {!readOnly ? <Button type="button" size="sm" className="h-8 w-8 p-0" variant="outline" title="Add required upload" onClick={() => setDocuments([...documents, `required_document_${documents.length + 1}`])}><Plus className="h-4 w-4" aria-hidden="true" /></Button> : null}
      </div>
      <div className="mt-2 space-y-2">
        {documents.map((documentCode, index) => (
          <div key={`${documentCode}-${index}`} className="grid grid-cols-[1fr_auto] gap-1">
            <input
              aria-label="Required upload"
              className="min-w-0 rounded-md border px-2 py-1 text-xs"
              disabled={readOnly}
              value={documentCode}
              onChange={(event) =>
                setDocuments(
                  documents.map((item, itemIndex) =>
                    itemIndex === index ? event.target.value : item
                  )
                )
              }
            />
            {!readOnly ? <Button type="button" size="sm" className="h-8 w-8 border-0 p-0" variant="outline" title={`Remove ${documentCode}`} onClick={() => setDocuments(documents.filter((_item, itemIndex) => itemIndex !== index))}><Trash2 className="h-4 w-4 text-red-600" aria-hidden="true" /></Button> : null}
          </div>
        ))}
        {documents.length === 0 ? <p className="text-xs text-gray-500">No required uploads added.</p> : null}
      </div>
    </div>
  );
}

function Toggle({ label, checked, disabled, onChange }: { label: string; checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="mt-2 flex items-center justify-between gap-3 text-xs font-medium">
      {label}
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
    </label>
  );
}

function WorkflowPreview({ editor }: { editor: WorkflowEditorState }) {
  return (
    <div className="border-b bg-gray-950 px-4 py-3 text-white">
      <div className="text-xs font-semibold uppercase text-gray-300">Preview</div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {editor.nodes.map((node, index) => (
          <div key={node.node_code} className="flex items-center gap-2">
            <span className="rounded-md border border-gray-600 bg-gray-900 px-2 py-1 text-sm">{node.display_name}</span>
            {index < editor.nodes.length - 1 ? <ArrowRight className="h-4 w-4 text-gray-400" aria-hidden="true" /> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function WorkflowNotice({ error, message }: { error: string | null; message: string | null }) {
  if (!error && !message) return null;
  return (
    <div className={`border-b px-4 py-3 text-sm ${error ? "border-red-200 bg-red-50 text-red-800" : "border-green-200 bg-green-50 text-green-800"}`} role={error ? "alert" : "status"}>
      {error ?? message}
    </div>
  );
}

function LifecyclePill({ state }: { state?: TenantWorkflowVersion["lifecycle_state"] }) {
  const tone = state === "published" ? "green" : state === "validated" ? "blue" : state === "draft" ? "amber" : "slate";
  return <Pill tone={tone}>{friendlyLifecycle(state)}</Pill>;
}

function friendlyLifecycle(state?: string) {
  return state ? friendlyCode(state) : "Not started";
}

function friendlyCode(value: string) {
  return value.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (character) => character.toUpperCase());
}

function transitionKey(transition: Pick<ApprovedWorkflowTransition, "from_status" | "to_status" | "action_code">) {
  return `${transition.from_status}|${transition.to_status}|${transition.action_code}`;
}

function requiresClientGovernance(
  transition: Pick<ApprovedWorkflowTransition, "allowed_roles" | "owner_role">
) {
  return transition.owner_role === "client" || transition.allowed_roles.includes("client");
}

function requiredDocumentCodes(value: Record<string, unknown>): string[] {
  const required = value.required;
  if (Array.isArray(required)) {
    return required
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          const row = item as Record<string, unknown>;
          return String(row.document_type ?? row.code ?? "").trim();
        }
        return "";
      })
      .filter(Boolean);
  }

  const documents = value.documents;
  if (Array.isArray(documents)) {
    return documents
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          const row = item as Record<string, unknown>;
          return String(row.document_type ?? row.code ?? "").trim();
        }
        return "";
      })
      .filter(Boolean);
  }

  return Object.entries(value)
    .filter(([, rawValue]) => rawValue === true)
    .map(([key]) => key)
    .filter(Boolean);
}
